import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

function bumpLoco(loco) {
  loco?.bump?.(400);
}

function getLenisScrollElement(lenis) {
  if (!lenis?.rootElement || !(lenis.rootElement instanceof Element)) {
    return document.documentElement;
  }
  return lenis.rootElement;
}

export function initOfferList(loco) {
  const section = document.querySelector("#oferta.card-grid");
  if (!section) return;

  const mediaEl = section.querySelector(".card-grid__media");
  const stackEl = mediaEl?.querySelector(".card-grid__media-stack");
  const items = gsap.utils.toArray(section.querySelectorAll(".card-grid__item"));
  const images = gsap.utils.toArray(section.querySelectorAll(".card-grid__media .thumb"));
  if (!items.length || !images.length || !mediaEl) return;

  const getViewportHeightPx = () => {
    const vv = window.visualViewport;
    if (vv && typeof vv.height === "number" && vv.height > 0) {
      return Math.round(vv.height);
    }
    return Math.round(window.innerHeight);
  };

  const applyOfferMediaViewportHeight = () => {
    const h = getViewportHeightPx();
    if (h < 1) return;
    const isDesktop = window.matchMedia("(min-width: 992px)").matches;
    const height = isDesktop ? h : Math.min(Math.round(h * 0.72), 640);
    mediaEl.style.minHeight = `${height}px`;
    mediaEl.style.height = `${height}px`;
    if (stackEl) {
      stackEl.style.minHeight = `${height}px`;
      stackEl.style.height = `${height}px`;
    }
  };

  let activeIndex = 0;

  const setActiveImage = (index) => {
    const i = Math.max(0, Math.min(index, images.length - 1));
    if (i === activeIndex && section.querySelector(".card-grid__item.is-active")) return;

    activeIndex = i;

    images.forEach((img, idx) => {
      img.classList.remove("active", "prev");
      if (idx === i) img.classList.add("active");
      else if (idx === i - 1) img.classList.add("prev");
    });

    items.forEach((item, idx) => {
      item.classList.toggle("is-active", idx === i);
    });
  };

  const bindScrollTriggers = () => {
    const lenis = loco?.lenisInstance;
    const scroller = lenis ? getLenisScrollElement(lenis) : window;
    const onScrollUpdate = () => ScrollTrigger.update();

    if (lenis) {
      ScrollTrigger.scrollerProxy(scroller, {
        scrollTop(value) {
          if (arguments.length) {
            lenis.scrollTo(value, { immediate: true });
          }
          return lenis.scroll;
        },
        getBoundingClientRect() {
          return {
            top: 0,
            left: 0,
            width: window.innerWidth,
            height: window.innerHeight,
          };
        },
      });

      if (typeof loco?.subscribeLenisScroll === "function") {
        loco.subscribeLenisScroll(onScrollUpdate);
      } else if (typeof lenis?.on === "function") {
        lenis.on("scroll", onScrollUpdate);
      } else {
        window.addEventListener("scroll", onScrollUpdate, { passive: true });
      }
    }

    const created = items.map((item, index) => {
      const triggerEl = item.querySelector(".card-grid__title") || item;
      return ScrollTrigger.create({
        scroller,
        trigger: triggerEl,
        start: "top 45%",
        end: "bottom 45%",
        invalidateOnRefresh: true,
        onEnter: () => setActiveImage(index),
        onEnterBack: () => setActiveImage(index),
      });
    });

    return () => {
      created.forEach((st) => st.kill());
      window.removeEventListener("scroll", onScrollUpdate);
    };
  };

  const bindIntersectionObserver = () => {
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible?.target) return;
        const idx = items.indexOf(visible.target);
        if (idx >= 0) setActiveImage(idx);
      },
      { root: null, threshold: [0.15, 0.35, 0.55], rootMargin: "-20% 0px -35% 0px" },
    );

    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  };

  const mm = gsap.matchMedia();
  let cleanup = null;

  mm.add("(min-width: 992px)", () => {
    setActiveImage(0);
    applyOfferMediaViewportHeight();
    cleanup = bindScrollTriggers();

    const refresh = () => {
      applyOfferMediaViewportHeight();
      ScrollTrigger.refresh();
      bumpLoco(loco);
    };

    requestAnimationFrame(refresh);
    setTimeout(refresh, 300);
    window.addEventListener("load", refresh, { once: true });

    return () => {
      cleanup?.();
      cleanup = null;
    };
  });

  mm.add("(max-width: 991px)", () => {
    setActiveImage(0);
    applyOfferMediaViewportHeight();
    cleanup = bindIntersectionObserver();
    return () => {
      cleanup?.();
      cleanup = null;
    };
  });

  const onOfferResize = () => {
    applyOfferMediaViewportHeight();
    ScrollTrigger.refresh();
    bumpLoco(loco);
  };

  window.addEventListener("resize", onOfferResize, { passive: true });
  if (window.visualViewport) {
    window.visualViewport.addEventListener("resize", onOfferResize);
  }

  applyOfferMediaViewportHeight();
  requestAnimationFrame(() => {
    applyOfferMediaViewportHeight();
    ScrollTrigger.refresh();
  });
}
